// Returns { expired: boolean, reason: string } indicating whether the shipment's
// scheduling window has closed or it has been delivered.
// Checks two signals: terminal status code and date-based window expiry.
// GRACE_DAYS extends each date deadline so drivers aren't locked out mid-delivery.
const GRACE_DAYS = 20

// Known terminal status codes that indicate the load is no longer actionable.
// TODO: verify the exact delivered/completed code value(s) from the backend enum.
const TERMINAL_STATUS_CODES = ['DEL', 'CANCELLED', 'AwaitingQuotes', 'DSP-Declined', 'DSP-Refused', 'IGNORE', 'CLOSED']

const active = (reason) => ({ expired: false, reason })
const expired = (reason) => ({ expired: true, reason })



function isShipmentExpired(ship) {
    if (!ship) return active('No shipment data was available.')

    if (TERMINAL_STATUS_CODES.length && TERMINAL_STATUS_CODES.includes(ship?.status?.code)) {
        return expired(`This shipment has a status of "${ship.status.code}".`)
    }

    // Gather every date field available across pickup and delivery.
    // If ANY is still within the grace window, keep the page active —
    // a single valid date overrides bad dates elsewhere.
    const rawDates = [
        { label: 'consignee.available1',            raw: ship?.consignee?.dates?.available1 },
        { label: 'consignee.available2',            raw: ship?.consignee?.dates?.available2 },
        { label: 'consignee.requestedDeliveryDate', raw: ship?.consignee?.dates?.requestedDeliveryDate },
        { label: 'shipper.available1',              raw: ship?.shipper?.dates?.available1 },
        { label: 'shipper.available2',              raw: ship?.shipper?.dates?.available2 },
    ]

    // For consolidations, collect dates from each stop using only fields relevant to that stop's type
    if (ship?.stops?.length) {
        ship.stops.forEach((stop, i) => {
            const prefix = `stop${stop.stopNumber ?? i}(${stop.type})`
            if (stop.type === 'Pickup') {
                rawDates.push({ label: `${prefix}.available1`, raw: stop?.dates?.available1 })
                rawDates.push({ label: `${prefix}.available2`, raw: stop?.dates?.available2 })
            } else {
                rawDates.push({ label: `${prefix}.requestedDeliveryDate`, raw: stop?.dates?.requestedDeliveryDate })
                rawDates.push({ label: `${prefix}.available1`,            raw: stop?.dates?.available1 })
                rawDates.push({ label: `${prefix}.available2`,            raw: stop?.dates?.available2 })
            }
        })
    }

    const candidateDates = rawDates
        .filter(d => d.raw)
        .map(d => ({ ...d, date: new Date(d.raw) }))
        .filter(d => !isNaN(d.date.getTime()))

    if (!candidateDates.length) {
        const createdRaw = ship?.dates?.created
        if (createdRaw) {
            const created = new Date(createdRaw)
            if (!isNaN(created.getTime())) {
                const today = new Date()
                today.setHours(0, 0, 0, 0)
                const daysSinceCreated = Math.floor((today - created) / (1000 * 60 * 60 * 24))
                if (daysSinceCreated > 365) {
                    return expired(`No scheduling dates were found on this shipment, and it was created over a year ago (${daysSinceCreated} days).`)
                }
            }
        }
        return active('No usable dates were found on this shipment.')
    }

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    let activeDate = null
    const allPast = candidateDates.every(d => {
        const withGrace = new Date(d.date)
        withGrace.setDate(withGrace.getDate() + GRACE_DAYS)
        const past = today > withGrace
        if (!past) activeDate = d
        return past
    })

    if (allPast) {
        return expired('All shipment dates are in the past and have passed the grace window.')
    }

    return active(`${activeDate.label} is still within the allowed scheduling window.`)
}

export default isShipmentExpired
